import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import { settingsApi, EmailSettings, DecimalSettings, PaymentGatewaySettings, PayOSConfig, MoMoConfig, VNPayConfig, ZaloPayConfig, VietQRConfig, BankTransferConfig, ShinhanConfig, MbPaygateConfig, MbbConfig, FCMSettings, StorageSettings } from '../services/settingsApi';
import { buildingsApi } from '../api/buildingsApi';
import {applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../utils/buildingSelection';
import { toast } from 'react-hot-toast';
import { QrSettingsPanel } from '../components/settings/QrSettingsPanel';
import { LogoSettingsPanel } from '../components/settings/LogoSettingsPanel';
import { getApiBaseUrl } from '../utils/apiBaseUrl';

const EMPTY_EMAIL_SETTINGS: EmailSettings = {
  providerType: 'SMTP',
  apiProvider: 'SENDIFY',
  apiKey: '',
  apiBaseUrl: '',
  smtpHost: '',
  smtpPort: 587,
  smtpSecure: false,
  smtpUser: '',
  smtpPass: '',
  smtpFrom: '',
};

const EMPTY_STORAGE_SETTINGS: StorageSettings = {
  storageType: 'local',
  s3Config: {
    name: '',
    endpoint: '',
    region: '',
    accessKey: '',
    secretKey: '',
    bucketName: '',
  },
};

/** Baked at build time from BACKEND_URL / AAPANEL_BACKEND_URL → REACT_APP_API_URL */
const API_BASE_URL = getApiBaseUrl();
const SHINHAN_WEBHOOK_BALANCE_NOTICE = `${API_BASE_URL}/payment-gateways/callback/shinhan/balance-notice`;
const SHINHAN_WEBHOOK_VA_NOTIFY = `${API_BASE_URL}/payment-gateways/callback/shinhan/notify`;

const Settings: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLang();
  const [activeTab, setActiveTab] = useState('email');
  // Default to 'global' for SUPERADMIN, 'user' for others
  const [settingsType, setSettingsType] = useState<'global' | 'user'>(
    user?.role === 'SUPERADMIN' ? 'global' : 'user'
  );
  const [selectedBuildingId, setSelectedBuildingId] = useState<string>('');
  const [buildings, setBuildings] = useState<any[]>([]);
  
  // Email Settings State
  const [emailSettings, setEmailSettings] = useState<EmailSettings>({
    providerType: 'SMTP',
    apiProvider: 'SENDIFY',
    apiKey: '',
    apiBaseUrl: '',
    smtpHost: '',
    smtpPort: 587,
    smtpSecure: false,
    smtpUser: '',
    smtpPass: '',
    smtpFrom: '',
  });
  const [effectiveEmailSettings, setEffectiveEmailSettings] = useState<EmailSettings | null>(null);
  const [hasOwnOperatorEmailSettings, setHasOwnOperatorEmailSettings] = useState(false);
  
  // Storage Settings State
  const [storageSettings, setStorageSettings] = useState<StorageSettings>({
    storageType: 'local',
    s3Config: {
      name: '',
      endpoint: '',
      region: '',
      accessKey: '',
      secretKey: '',
      bucketName: '',
    }
  });
  const [effectiveStorageSettings, setEffectiveStorageSettings] = useState<StorageSettings | null>(null);
  const [hasOwnOperatorStorageSettings, setHasOwnOperatorStorageSettings] = useState(false);
  
  // Decimal Settings State
  const [decimalSettings, setDecimalSettings] = useState<DecimalSettings>({
    amountDecimalPlaces: 0,
    vatDecimalPlaces: 0,
  });
  
  // Payment Gateway Settings State
  const [paymentGatewaySettings, setPaymentGatewaySettings] = useState<PaymentGatewaySettings[]>([]);
  const [selectedGateway, setSelectedGateway] = useState<string>('PAYOS');

  // FCM Settings State
  const [fcmSettings, setFcmSettings] = useState<FCMSettings>({
    serviceAccount: {},
    isEnabled: false,
  });
  const [fcmJsonInput, setFcmJsonInput] = useState<string>('');
  const [fcmHasStoredAccount, setFcmHasStoredAccount] = useState(false);
  const [fcmEditingJson, setFcmEditingJson] = useState(false);
  const [gatewayConfigs, setGatewayConfigs] = useState<{
    PAYOS: PayOSConfig;
    MOMO: MoMoConfig;
    VNPAY: VNPayConfig;
    ZALOPAY: ZaloPayConfig;
    VIETQR: VietQRConfig;
    BANK_TRANSFER: BankTransferConfig;
    SHINHAN: ShinhanConfig;
    MB_PAYGATE: MbPaygateConfig;
    MBB: MbbConfig;
  }>({
    PAYOS: {
      clientId: '',
      apiKey: '',
      checksumKey: '',
      environment: 'sandbox',
    },
    MOMO: {
      partnerCode: '',
      accessKey: '',
      secretKey: '',
      environment: 'sandbox',
    },
    VNPAY: {
      tmnCode: '',
      secretKey: '',
      environment: 'sandbox',
    },
    ZALOPAY: {
      appId: '',
      key1: '',
      key2: '',
      environment: 'sandbox',
    },
    VIETQR: {
      username: '',
      password: '',
      merchantUsername: '',
      merchantPassword: '',
      bankCode: '',
      bankAccount: '',
      userBankName: '',
      environment: 'sandbox',
    },
    BANK_TRANSFER: {
      bankName: '',
      accountNumber: '',
      accountName: '',
      qrCode: '',
    },
    SHINHAN: {
      clientId: '',
      clientSecret: '',
      motherAccountNumber: '',
      vaPrefix: '',
      merchantName: '',
      privateKey: '',
      shinhanPublicKey: '',
      bankBin: '970424',
      bankName: 'NH TNHH MTV Shinhan Việt Nam',
      environment: 'sandbox',
      mockMode: true,
      useVirtualAccount: false,
      useBuildingVirtualAccount: true,
    },
    MB_PAYGATE: {
      clientId: '',
      clientSecret: '',
      merchantId: '',
      accessCode: '',
      secretKey: '',
      environment: 'sandbox',
      defaultPayMethod: 'QR',
      orderRefChannel: '4N',
    },
    MBB: {
      username: '',
      password: '',
      corpId: '',
      accountNumber: '',
      anticaptchaApiKey: '',
      bankName: 'Ngân hàng TMCP Quân đội (MB Bank)',
      bankBin: '970422',
    },
  });
  const [gatewayEnabled, setGatewayEnabled] = useState<{[key: string]: boolean}>({
    PAYOS: false,
    MOMO: false,
    VNPAY: false,
    ZALOPAY: false,
    VIETQR: false,
    BANK_TRANSFER: false,
    SHINHAN: false,
    MB_PAYGATE: false,
    MBB: false,
  });
  
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);



  useEffect(() => {
    loadBuildings();
  }, []);

  useEffect(() => {
    loadSettings();
  }, [activeTab, settingsType, selectedBuildingId]);

  const loadBuildings = async () => {
    try {
      const buildings = await buildingsApi.getBuildings();
      setBuildings(buildings);
      setSelectedBuildingId((prev) => applyDefaultBuildingSelection(buildings, user, prev));
    } catch (error) {
      console.error('Failed to load buildings:', error);
      toast.error(t('failedToLoad'));
    }
  };

  const loadSettings = async () => {
    setLoading(true);
    try {
      if (activeTab === 'email') {
        const scopedBuildingId = settingsType === 'global' ? undefined : selectedBuildingId || undefined;
        if (settingsType === 'global') {
          const currentSettings = await settingsApi.getEmailSettings(true);
          setEmailSettings(currentSettings || EMPTY_EMAIL_SETTINGS);
          setEffectiveEmailSettings(null);
          setHasOwnOperatorEmailSettings(false);
        } else {
          const ownSettings = await settingsApi.getUserEmailSettings(scopedBuildingId);
          setHasOwnOperatorEmailSettings(!!ownSettings);
          setEmailSettings(ownSettings || EMPTY_EMAIL_SETTINGS);
          const effective = await settingsApi.getEffectiveEmailSettings(scopedBuildingId);
          setEffectiveEmailSettings(effective);
        }
      } else if (activeTab === 'storage') {
        const scopedBuildingId = settingsType === 'global' ? undefined : selectedBuildingId || undefined;
        if (settingsType === 'global') {
          const currentSettings = await settingsApi.getStorageSettings(true);
          setStorageSettings(currentSettings || EMPTY_STORAGE_SETTINGS);
          setEffectiveStorageSettings(null);
          setHasOwnOperatorStorageSettings(false);
        } else {
          const ownSettings = await settingsApi.getUserStorageSettings(scopedBuildingId);
          setHasOwnOperatorStorageSettings(!!ownSettings);
          setStorageSettings(ownSettings || EMPTY_STORAGE_SETTINGS);
          const effective = await settingsApi.getEffectiveStorageSettings(scopedBuildingId);
          setEffectiveStorageSettings(effective);
        }
      } else if (activeTab === 'decimal') {
        // Load decimal settings
        const settings = await settingsApi.getDecimalSettings();
        if (settings) {
          setDecimalSettings(settings);
        }
      } else if (activeTab === 'gateways') {
        // Load payment gateway settings
        const settings = await settingsApi.getPaymentGatewaySettings(
          settingsType === 'global',
          settingsType === 'global' ? undefined : selectedBuildingId || undefined,
        );
        setPaymentGatewaySettings(settings);

        const newConfigs = {
          PAYOS: { clientId: '', apiKey: '', checksumKey: '', environment: 'sandbox' as const },
          MOMO: { partnerCode: '', accessKey: '', secretKey: '', environment: 'sandbox' as const },
          VNPAY: { tmnCode: '', secretKey: '', environment: 'sandbox' as const },
          ZALOPAY: { appId: '', key1: '', key2: '', environment: 'sandbox' as const },
          VIETQR: {
            username: '', password: '', merchantUsername: '', merchantPassword: '',
            bankCode: '', bankAccount: '', userBankName: '', environment: 'sandbox' as const,
          },
          BANK_TRANSFER: { bankName: '', accountNumber: '', accountName: '', qrCode: '' },
          SHINHAN: {
            clientId: '', clientSecret: '', motherAccountNumber: '', vaPrefix: '',
            merchantName: '', privateKey: '', shinhanPublicKey: '', bankBin: '970424',
            bankName: 'NH TNHH MTV Shinhan Việt Nam', environment: 'sandbox' as const,
            mockMode: true, useVirtualAccount: false, useBuildingVirtualAccount: true,
          },
          MB_PAYGATE: {
            clientId: '', clientSecret: '', merchantId: '', accessCode: '', secretKey: '',
            environment: 'sandbox' as const, defaultPayMethod: 'QR', orderRefChannel: '4N',
          },
          MBB: {
            username: '', password: '', corpId: '', accountNumber: '',
            anticaptchaApiKey: '',
            bankName: 'Ngân hàng TMCP Quân đội (MB Bank)', bankBin: '970422',
          },
        };
        const newEnabled: { [key: string]: boolean } = {
          PAYOS: false, MOMO: false, VNPAY: false, ZALOPAY: false,
          VIETQR: false, BANK_TRANSFER: false, SHINHAN: false, MB_PAYGATE: false, MBB: false,
        };

        settings.forEach((setting) => {
          newConfigs[setting.gatewayType as keyof typeof newConfigs] = setting.config;
          newEnabled[setting.gatewayType] = setting.isEnabled;
        });

        setGatewayConfigs(newConfigs);
        setGatewayEnabled(newEnabled);
      } else if (activeTab === 'qr') {
        // QR settings loaded inside QrSettingsPanel
      } else if (activeTab === 'logo') {
        // Logo settings loaded inside LogoSettingsPanel
      } else if (activeTab === 'fcm' && user?.role === 'SUPERADMIN') {
        // Load FCM settings
        const settings = await settingsApi.getFCMSettings();
        if (settings) {
          setFcmSettings(settings);
          setFcmHasStoredAccount(Boolean(settings.hasServiceAccount));
          setFcmEditingJson(false);
          setFcmJsonInput('');
        } else {
          setFcmHasStoredAccount(false);
          setFcmEditingJson(false);
          setFcmJsonInput('');
        }
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Không thể tải cài đặt');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEmailSettings = async () => {
    const isApiProvider = emailSettings.providerType === 'API';
    const missingRequired = isApiProvider
      ? !emailSettings.apiKey || !emailSettings.smtpFrom
      : !emailSettings.smtpHost || !emailSettings.smtpUser || !emailSettings.smtpFrom;

    if (missingRequired || (isApiProvider && emailSettings.apiProvider === 'CUSTOM' && !emailSettings.apiBaseUrl)) {
      toast.error('⚠️ Vui lòng điền đầy đủ thông tin bắt buộc');
      return;
    }

    setLoading(true);
    const loadingToast = toast.loading('🔄 Đang lưu cài đặt email...');
    
    try {
      await settingsApi.saveEmailSettings({
        ...emailSettings,
        global: settingsType === 'global',
        buildingId: settingsType === 'global' ? undefined : selectedBuildingId || undefined,
      });
      
      toast.dismiss(loadingToast);
      
      const settingTypeText = settingsType === 'global' ? 'toàn cục' : 'operator';
      const successMessage = settingsType === 'global'
        ? `✅ Lưu cài đặt email ${settingTypeText} thành công!`
        : '✅ Lưu cài đặt email operator thành công!';
      
      toast.success(successMessage, { duration: 4000 });
      
      loadSettings();
    } catch (error: any) {
      toast.dismiss(loadingToast);
      console.error('Failed to save email settings:', error);
      
      const errorMessage = error?.response?.data?.message || error?.message || 'Lỗi không xác định';
      toast.error(`❌ Không thể lưu cài đặt email: ${errorMessage}`, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handleTestEmailConnection = async () => {
    const isApiProvider = emailSettings.providerType === 'API';
    const hasSettings = isApiProvider
      ? Boolean(emailSettings.apiKey && emailSettings.smtpFrom)
      : Boolean(emailSettings.smtpHost && emailSettings.smtpUser && emailSettings.smtpPass);
    if (!hasSettings) {
      toast.error(isApiProvider
        ? '⚠️ Vui lòng điền API Key và Email gửi trước khi kiểm tra kết nối'
        : '⚠️ Vui lòng điền thông tin SMTP trước khi kiểm tra kết nối');
      return;
    }

    setTestingConnection(true);
    const loadingToast = toast.loading('🔄 Đang kiểm tra kết nối email...');
    
    try {
      const result = await settingsApi.testEmailConnection(
        settingsType === 'global',
        settingsType === 'global' ? undefined : selectedBuildingId || undefined,
      );
      toast.dismiss(loadingToast);
      
      const settingTypeText = settingsType === 'global' ? 'toàn cục' : 'operator';
      if (result.success) {
        toast.success(`✅ Kiểm tra kết nối email ${settingTypeText} thành công!`, { duration: 4000 });
      } else {
        toast.error(`❌ Kết nối email ${settingTypeText} thất bại: ${result.message}`, { duration: 6000 });
      }
    } catch (error: any) {
      toast.dismiss(loadingToast);
      console.error('Failed to test email connection:', error);
      
      const errorMessage = error?.response?.data?.message || error?.message || 'Lỗi không xác định';
      const settingTypeText = settingsType === 'global' ? 'toàn cục' : 'cá nhân';
      
      toast.error(`❌ Không thể kiểm tra kết nối email ${settingTypeText}: ${errorMessage}`, { duration: 6000 });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSaveStorageSettings = async () => {
    // Validate S3 settings if S3 is selected
    if (storageSettings.storageType === 's3') {
      const { name, endpoint, region, accessKey, secretKey, bucketName } = storageSettings.s3Config;
      if (!name || !endpoint || !region || !accessKey || !secretKey || !bucketName) {
        toast.error('⚠️ Vui lòng điền đầy đủ thông tin S3 storage');
        return;
      }
    }

    setLoading(true);
    const loadingToast = toast.loading('🔄 Đang lưu cài đặt lưu trữ...');
    
    try {
      await settingsApi.saveStorageSettings({
        ...storageSettings,
        global: settingsType === 'global',
        buildingId: settingsType === 'global' ? undefined : selectedBuildingId || undefined,
      });
      
      toast.dismiss(loadingToast);
      
      const settingTypeText = settingsType === 'global' ? 'toàn cục' : 'operator';
      const storageTypeText = storageSettings.storageType === 'local' ? 'Local Storage' : 'S3 Storage';
      toast.success(`✅ Lưu cài đặt lưu trữ (${storageTypeText}) ${settingTypeText} thành công!`, { duration: 4000 });
      
      loadSettings();
    } catch (error: any) {
      toast.dismiss(loadingToast);
      console.error('Failed to save storage settings:', error);
      
      const errorMessage = error?.response?.data?.message || error?.message || 'Lỗi không xác định';
      toast.error(`❌ Không thể lưu cài đặt lưu trữ: ${errorMessage}`, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDecimalSettings = async () => {
    setLoading(true);
    const loadingToast = toast.loading('🔄 Đang lưu cài đặt decimal...');
    
    try {
      await settingsApi.saveDecimalSettings(decimalSettings);
      toast.dismiss(loadingToast);
      toast.success('✅ Lưu cài đặt decimal thành công!', { duration: 4000 });
      loadSettings();
    } catch (error: any) {
      toast.dismiss(loadingToast);
      console.error('Failed to save decimal settings:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Lỗi không xác định';
      toast.error(`❌ Không thể lưu cài đặt decimal: ${errorMessage}`, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGatewaySettings = async () => {
    setLoading(true);
    const loadingToast = toast.loading('🔄 Đang lưu cài đặt payment gateway...');
    
    try {
      const config = gatewayConfigs[selectedGateway as keyof typeof gatewayConfigs];
      const isEnabled = gatewayEnabled[selectedGateway];
      
      await settingsApi.savePaymentGatewaySettings({
        gatewayType: selectedGateway,
        config,
        isEnabled,
        global: settingsType === 'global',
        buildingId: settingsType === 'global' ? undefined : selectedBuildingId || undefined,
      });
      
      toast.dismiss(loadingToast);
      toast.success(`✅ Lưu cài đặt ${selectedGateway} thành công!`, { duration: 4000 });
      loadSettings();
    } catch (error: any) {
      toast.dismiss(loadingToast);
      console.error('Failed to save gateway settings:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Lỗi không xác định';
      toast.error(`❌ Không thể lưu cài đặt gateway: ${errorMessage}`, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFCMSettings = async () => {
    setLoading(true);
    const loadingToast = toast.loading('🔄 Đang lưu cài đặt FCM...');
    
    try {
      let serviceAccount: object | undefined;
      if (fcmJsonInput.trim()) {
        try {
          serviceAccount = JSON.parse(fcmJsonInput);
        } catch (parseError) {
          toast.dismiss(loadingToast);
          toast.error('❌ JSON Service Account không hợp lệ');
          return;
        }
      } else if (fcmEditingJson || !fcmHasStoredAccount) {
        serviceAccount = {};
      }

      await settingsApi.saveFCMSettings({
        ...(serviceAccount !== undefined ? { serviceAccount } : {}),
        isEnabled: fcmSettings.isEnabled,
      });
      
      toast.dismiss(loadingToast);
      toast.success('✅ Lưu cài đặt FCM thành công!', { duration: 4000 });
      loadSettings();
    } catch (error: any) {
      toast.dismiss(loadingToast);
      console.error('Failed to save FCM settings:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Lỗi không xác định';
      toast.error(`❌ Không thể lưu cài đặt FCM: ${errorMessage}`, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handleTestFCMConnection = async () => {
    setTestingConnection(true);
    const loadingToast = toast.loading('🔄 Đang kiểm tra kết nối FCM...');

    try {
      const result = await settingsApi.testFCMConnection();
      toast.dismiss(loadingToast);

      if (result.success) {
        toast.success('✅ Kiểm tra kết nối FCM thành công!', { duration: 4000 });
      } else {
        toast.error(`❌ Kết nối FCM thất bại: ${result.message}`, { duration: 6000 });
      }
    } catch (error: any) {
      toast.dismiss(loadingToast);
      console.error('Failed to test FCM connection:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Lỗi không xác định';
      toast.error(`❌ Không thể kiểm tra kết nối FCM: ${errorMessage}`, { duration: 6000 });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleTestMbPaygateConnection = async () => {
    setTestingConnection(true);
    const loadingToast = toast.loading('🔄 Đang kiểm tra kết nối MB Paygate...');

    try {
      const result = await settingsApi.testMbPaygateConnection({
        config: gatewayConfigs.MB_PAYGATE,
        global: settingsType === 'global',
        buildingId: settingsType === 'global' ? undefined : selectedBuildingId || undefined,
      });
      toast.dismiss(loadingToast);

      if (result.success) {
        toast.success(`✅ ${result.message || 'Kiểm tra kết nối MB Paygate thành công!'}`, { duration: 5000 });
      } else {
        toast.error(`❌ Kết nối MB Paygate thất bại: ${result.message}`, { duration: 6000 });
      }
    } catch (error: any) {
      toast.dismiss(loadingToast);
      console.error('Failed to test MB Paygate connection:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Lỗi không xác định';
      toast.error(`❌ Không thể kiểm tra kết nối MB Paygate: ${errorMessage}`, { duration: 6000 });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleTestMbbConnection = async () => {
    setTestingConnection(true);
    const loadingToast = toast.loading('🔄 Đang kiểm tra kết nối MB Bank...');

    try {
      const result = await settingsApi.testMbbConnection({
        config: gatewayConfigs.MBB,
        global: settingsType === 'global',
        buildingId: settingsType === 'global' ? undefined : selectedBuildingId || undefined,
      });
      toast.dismiss(loadingToast);

      if (result.success) {
        toast.success(`✅ ${result.message || 'Kiểm tra kết nối MB Bank thành công!'}`, { duration: 5000 });
      } else {
        toast.error(`❌ Kết nối MB Bank thất bại: ${result.message}`, { duration: 6000 });
      }
    } catch (error: any) {
      toast.dismiss(loadingToast);
      console.error('Failed to test MBB connection:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Lỗi không xác định';
      toast.error(`❌ Không thể kiểm tra kết nối MB Bank: ${errorMessage}`, { duration: 6000 });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleTestShinhanConnection = async () => {
    setTestingConnection(true);
    const loadingToast = toast.loading('🔄 Đang kiểm tra kết nối Shinhan Bank...');

    try {
      const result = await settingsApi.testShinhanConnection({
        config: gatewayConfigs.SHINHAN,
        global: settingsType === 'global',
        buildingId: settingsType === 'global' ? undefined : selectedBuildingId || undefined,
      });
      toast.dismiss(loadingToast);

      if (result.success) {
        toast.success(`✅ ${result.message || 'Kiểm tra kết nối Shinhan thành công!'}`, { duration: 5000 });
      } else {
        toast.error(`❌ Kết nối Shinhan thất bại: ${result.message}`, { duration: 6000 });
      }
    } catch (error: any) {
      toast.dismiss(loadingToast);
      console.error('Failed to test Shinhan connection:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Lỗi không xác định';
      toast.error(`❌ Không thể kiểm tra kết nối Shinhan: ${errorMessage}`, { duration: 6000 });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleDeleteSettings = async (type: 'email' | 'storage') => {
    const settingTypeText = settingsType === 'global' ? 'toàn cục' : 'operator';
    const typeText = type === 'email' ? 'email' : 'lưu trữ';

    const buildingName = selectedBuildingId
      ? buildings.find((b) => b.id === selectedBuildingId)?.name
      : '';

    const confirmMessage = settingsType === 'user'
      ? buildingName
        ? `Bạn có chắc chắn muốn xóa cài đặt ${typeText} riêng cho tòa nhà ${buildingName}?\n\nHệ thống sẽ dùng cài đặt operator hoặc toàn cục.`
        : `Bạn có chắc chắn muốn xóa cài đặt ${typeText} operator?\n\nHệ thống sẽ dùng lại cài đặt toàn cục từ SUPERADMIN.`
      : `Bạn có chắc chắn muốn xóa cài đặt ${typeText} ${settingTypeText}?\n\nHành động này không thể hoàn tác!`;
    
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    const loadingToast = toast.loading(`🔄 Đang xóa cài đặt ${typeText}...`);
    
    try {
      if (type === 'email') {
        await settingsApi.deleteEmailSettings(
          settingsType === 'global',
          settingsType === 'global' ? undefined : selectedBuildingId || undefined,
        );
        setEmailSettings(EMPTY_EMAIL_SETTINGS);
        setHasOwnOperatorEmailSettings(false);
      } else {
        await settingsApi.deleteStorageSettings(
          settingsType === 'global',
          settingsType === 'global' ? undefined : selectedBuildingId || undefined,
        );
        setStorageSettings(EMPTY_STORAGE_SETTINGS);
        setHasOwnOperatorStorageSettings(false);
      }
      
      toast.dismiss(loadingToast);
      const successMessage = settingsType === 'user'
        ? buildingName
          ? `✅ Đã xóa cài đặt ${typeText} riêng cho tòa nhà ${buildingName}.`
          : `✅ Đã xóa cài đặt ${typeText} operator — hệ thống dùng cài đặt toàn cục.`
        : `✅ Xóa cài đặt ${typeText} ${settingTypeText} thành công!`;
      
      toast.success(successMessage, { duration: 3000 });
      
      loadSettings();
    } catch (error: any) {
      toast.dismiss(loadingToast);
      console.error('Failed to delete settings:', error);
      
      const errorMessage = error?.response?.data?.message || error?.message || 'Lỗi không xác định';
      toast.error(`❌ Không thể xóa cài đặt ${typeText} ${settingTypeText}: ${errorMessage}`, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const renderScopedBuildingSelector = () => (
    settingsType === 'user' && shouldShowBuildingSelector(buildings) ? (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('selectBuildingForSettings')}
        </label>
        <select
          value={selectedBuildingId}
          onChange={(e) => selectBuilding(user?.id, e.target.value, setSelectedBuildingId)}
                disabled={!needsBuildingSelection(buildings)}
          className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">-- {t('selectBuildingForSettings')} (tất cả tòa nhà) --</option>
          {buildings.map((building) => (
            <option key={building.id} value={building.id}>
              {building.name}
            </option>
          ))}
        </select>
        {!selectedBuildingId && (
          <p className="text-sm text-blue-600 mt-1">
            Không chọn tòa nhà: cài đặt áp dụng cho tất cả chung cư của operator (trừ khi đã có cài đặt riêng theo tòa).
          </p>
        )}
        {selectedBuildingId && (
          <p className="text-sm text-amber-700 mt-1">
            Đang cấu hình riêng cho tòa nhà này. Bấm <strong>Xóa cài đặt</strong> để dùng cài đặt operator hoặc toàn cục.
          </p>
        )}
      </div>
    ) : null
  );

  const renderEmailSettings = () => (
    <div className="space-y-6">
      {/* Settings Type Selector */}
      <div className="space-y-4 border-b border-gray-200 pb-4">
        <div className="flex space-x-4">
          {user?.role === 'SUPERADMIN' && (
            <button
              onClick={() => setSettingsType('global')}
              className={`px-4 py-2 rounded-md ${
                settingsType === 'global'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('globalSettings')}
            </button>
          )}
          <button
            onClick={() => setSettingsType('user')}
            className={`px-4 py-2 rounded-md ${
              settingsType === 'user'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('personalSettings')}
          </button>
        </div>

        {renderScopedBuildingSelector()}

        {settingsType === 'user' && (
          <p className="text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md p-3">
            Form bên dưới chỉ hiển thị cài đặt riêng tại phạm vi đã chọn — không hiển thị giá trị toàn cục.
            Bấm <strong>Xóa cài đặt</strong> để quay về cài đặt cấp cao hơn.
          </p>
        )}

        {settingsType === 'global' && user?.role === 'SUPERADMIN' && (
          <p className="text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md p-3">
            Cài đặt toàn cục làm mặc định khi operator chưa cấu hình riêng.
          </p>
        )}
      </div>

      {settingsType === 'user' && effectiveEmailSettings && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <h4 className="font-medium text-blue-900 mb-2">Đang chạy trên hệ thống:</h4>
          <div className="text-sm text-blue-700">
            <p><strong>{t('emailProviderType')}:</strong> {effectiveEmailSettings.providerType === 'API' ? t('emailProviderApi') : t('emailProviderSmtp')}</p>
            {effectiveEmailSettings.providerType === 'API' ? (
              <>
                <p><strong>{t('emailApiProvider')}:</strong> {effectiveEmailSettings.apiProvider || 'SENDIFY'}</p>
                {effectiveEmailSettings.apiBaseUrl && (
                  <p><strong>{t('emailApiBaseUrl')}:</strong> {effectiveEmailSettings.apiBaseUrl}</p>
                )}
              </>
            ) : (
              <>
                <p><strong>SMTP Host:</strong> {effectiveEmailSettings.smtpHost}</p>
                <p><strong>Port:</strong> {effectiveEmailSettings.smtpPort}</p>
                <p><strong>SSL/TLS:</strong> {effectiveEmailSettings.smtpSecure ? 'Enabled' : 'Disabled'}</p>
              </>
            )}
            <p><strong>{t('smtpFromEmail')}:</strong> {effectiveEmailSettings.smtpFrom}</p>
            <p className="text-xs mt-2">
              {hasOwnOperatorEmailSettings
                ? (selectedBuildingId ? 'Đang dùng cài đặt riêng theo tòa nhà' : 'Đang dùng cài đặt operator')
                : selectedBuildingId
                  ? 'Đang dùng cài đặt operator hoặc toàn cục (chưa có cài đặt riêng theo tòa)'
                  : 'Đang dùng cài đặt toàn cục (chưa có cài đặt operator)'}
            </p>
          </div>
        </div>
      )}

      {settingsType === 'user' && !hasOwnOperatorEmailSettings && (
        <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-3">
          Chưa có cài đặt email riêng tại phạm vi này. Điền form và Lưu để tạo cài đặt, hoặc để trống và dùng cài đặt cấp cao hơn.
        </p>
      )}

      {/* Email Settings Form */}
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('emailProviderType')} *
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className={`flex items-start space-x-3 p-4 border rounded-md cursor-pointer ${emailSettings.providerType !== 'API' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>
              <input
                type="radio"
                name="emailProviderType"
                value="SMTP"
                checked={emailSettings.providerType !== 'API'}
                onChange={() => setEmailSettings({ ...emailSettings, providerType: 'SMTP' })}
                className="mt-1"
              />
              <span>
                <span className="block font-medium text-gray-900">{t('emailProviderSmtp')}</span>
                <span className="block text-xs text-gray-500 mt-1">{t('emailProviderSmtpDescription')}</span>
              </span>
            </label>
            <label className={`flex items-start space-x-3 p-4 border rounded-md cursor-pointer ${emailSettings.providerType === 'API' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>
              <input
                type="radio"
                name="emailProviderType"
                value="API"
                checked={emailSettings.providerType === 'API'}
                onChange={() => setEmailSettings({ ...emailSettings, providerType: 'API', apiProvider: emailSettings.apiProvider || 'SENDIFY' })}
                className="mt-1"
              />
              <span>
                <span className="block font-medium text-gray-900">{t('emailProviderApi')}</span>
                <span className="block text-xs text-gray-500 mt-1">{t('emailProviderApiDescription')}</span>
              </span>
            </label>
          </div>
        </div>

        {emailSettings.providerType === 'API' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('emailApiProvider')} *
              </label>
              <select
                value={emailSettings.apiProvider || 'SENDIFY'}
                onChange={(e) => setEmailSettings({
                  ...emailSettings,
                  apiProvider: e.target.value as EmailSettings['apiProvider'],
                })}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="SENDIFY">{t('emailApiProviderSendify')}</option>
                <option value="RESEND">{t('emailApiProviderResend')}</option>
                <option value="CUSTOM">{t('emailApiProviderCustom')}</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">{t('emailApiProviderDescription')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('emailApiKey')} *
              </label>
              <input
                type="password"
                value={emailSettings.apiKey || ''}
                onChange={(e) => setEmailSettings({ ...emailSettings, apiKey: e.target.value })}
                placeholder="sk_..."
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {emailSettings.apiProvider === 'CUSTOM' && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('emailApiBaseUrl')} *
                </label>
                <input
                  type="url"
                  value={emailSettings.apiBaseUrl || ''}
                  onChange={(e) => setEmailSettings({ ...emailSettings, apiBaseUrl: e.target.value })}
                  placeholder="https://api.example.com"
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">{t('emailApiBaseUrlDescription')}</p>
              </div>
            )}
          </div>
        )}

        {emailSettings.providerType !== 'API' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('smtpHost')} *
              </label>
              <input
                type="text"
                value={emailSettings.smtpHost}
                onChange={(e) => setEmailSettings({ ...emailSettings, smtpHost: e.target.value })}
                placeholder="smtp.gmail.com"
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('smtpPort')} *
              </label>
              <input
                type="number"
                value={emailSettings.smtpPort}
                onChange={(e) => setEmailSettings({ ...emailSettings, smtpPort: parseInt(e.target.value) })}
                placeholder="587"
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={emailSettings.smtpSecure}
                  onChange={(e) => setEmailSettings({ ...emailSettings, smtpSecure: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  {t('smtpSecure')}
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-1">
                {t('smtpSecureDescription')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('smtpUsername')} *
              </label>
              <input
                type="text"
                value={emailSettings.smtpUser}
                onChange={(e) => setEmailSettings({ ...emailSettings, smtpUser: e.target.value })}
                placeholder="your-email@example.com"
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('smtpPassword')} *
              </label>
              <input
                type="password"
                value={emailSettings.smtpPass}
                onChange={(e) => setEmailSettings({ ...emailSettings, smtpPass: e.target.value })}
                placeholder="••••••••"
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>
        )}

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('smtpFromEmail')} *
          </label>
          <input
            type="text"
            value={emailSettings.smtpFrom}
            onChange={(e) => setEmailSettings({ ...emailSettings, smtpFrom: e.target.value })}
            placeholder='"Building Management" <noreply@example.com>'
            className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            required
          />
          {emailSettings.providerType === 'API' && (
            <p className="text-xs text-gray-500 mt-1">{t('emailApiFromDescription')}</p>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4">
        <button
          onClick={handleSaveEmailSettings}
          disabled={loading}
          className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 disabled:opacity-50 flex items-center space-x-2 transition-all duration-200"
        >
          {loading ? (
            <>
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>{t('savingSettings')}</span>
            </>
          ) : (
            <>
              <span>💾</span>
              <span>{t('saveSettings')}</span>
            </>
          )}
        </button>

        <button
          onClick={handleTestEmailConnection}
          disabled={testingConnection || loading}
          className="bg-green-500 text-white px-6 py-2 rounded-md hover:bg-green-600 disabled:opacity-50 flex items-center space-x-2 transition-all duration-200"
        >
          {testingConnection ? (
            <>
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>{t('testingConnection')}</span>
            </>
          ) : (
            <>
              <span>🔗</span>
              <span>{t('testConnection')}</span>
            </>
          )}
        </button>

        <button
          onClick={() => handleDeleteSettings('email')}
          disabled={loading}
          className="bg-red-500 text-white px-6 py-2 rounded-md hover:bg-red-600 disabled:opacity-50 flex items-center space-x-2 transition-all duration-200"
        >
          <span>🗑️</span>
          <span>{t('deleteSettings')}</span>
        </button>
      </div>
    </div>
  );

  const renderPaymentGatewaySettings = () => (
    <div className="space-y-6">
      {/* Settings Type Selector */}
      <div className="space-y-4 border-b border-gray-200 pb-4">
        <div className="flex space-x-4">
          {user?.role === 'SUPERADMIN' && (
            <button
              onClick={() => setSettingsType('global')}
              className={`px-4 py-2 rounded-md ${
                settingsType === 'global'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('globalSettings')}
            </button>
          )}
          <button
            onClick={() => setSettingsType('user')}
            className={`px-4 py-2 rounded-md ${
              settingsType === 'user'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('personalSettings')}
          </button>
        </div>

        {settingsType === 'user' && shouldShowBuildingSelector(buildings) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('selectBuildingForSettings')}
            </label>
            <select
              value={selectedBuildingId}
              onChange={(e) => selectBuilding(user?.id, e.target.value, setSelectedBuildingId)}
                disabled={!needsBuildingSelection(buildings)}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">-- {t('selectBuildingForSettings')} (tất cả tòa nhà) --</option>
              {buildings.map((building) => (
                <option key={building.id} value={building.id}>
                  {building.name}
                </option>
              ))}
            </select>
            {!selectedBuildingId && (
              <p className="text-sm text-blue-600 mt-1">
                Không chọn tòa nhà: cài đặt áp dụng cho tất cả tòa nhà của bạn (trừ khi đã có cài đặt riêng theo tòa).
              </p>
            )}
            {selectedBuildingId && (
              <p className="text-sm text-amber-700 mt-1">
                Đang cấu hình riêng cho tòa nhà này. Số VA Shinhan vẫn cấu hình tại tab <strong>Cài đặt QR thanh toán</strong>.
              </p>
            )}
          </div>
        )}

        {settingsType === 'global' && user?.role === 'SUPERADMIN' && (
          <p className="text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md p-3">
            Cài đặt toàn cục làm mặc định khi operator chưa cấu hình riêng.
          </p>
        )}
      </div>

      {/* Gateway Selection */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Cấu hình Payment Gateway</h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-6">
          {[
            { key: 'PAYOS', name: 'PayOS QR Code', icon: '💳' },
            { key: 'MOMO', name: 'MoMo', icon: '📱' },
            { key: 'VNPAY', name: 'VNPay', icon: '🏦' },
            { key: 'ZALOPAY', name: 'ZaloPay', icon: '💰' },
            { key: 'VIETQR', name: 'VietQR', icon: '🇻🇳' },
            { key: 'SHINHAN', name: 'Shinhan Bank', icon: '🏛️' },
            { key: 'MB_PAYGATE', name: 'MB Paygate', icon: '💳' },
            { key: 'MBB', name: 'MB Bank DN', icon: '🏦' },
            { key: 'BANK_TRANSFER', name: 'Chuyển khoản', icon: '🏧' },
          ].map((gateway) => (
            <button
              key={gateway.key}
              onClick={() => setSelectedGateway(gateway.key)}
              className={`p-4 border rounded-lg text-center transition-all ${
                selectedGateway === gateway.key
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="text-2xl mb-2">{gateway.icon}</div>
              <div className="font-medium">{gateway.name}</div>
              <div className="text-sm mt-1">
                <label
                  className="flex items-center justify-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={gatewayEnabled[gateway.key] || false}
                    onChange={(e) => {
                      e.stopPropagation();
                      setGatewayEnabled(prev => ({
                        ...prev,
                        [gateway.key]: e.target.checked
                      }));
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="mr-2"
                  />
                  Kích hoạt
                </label>
              </div>
            </button>
          ))}
        </div>

        {/* Gateway Configuration Forms */}
        {selectedGateway === 'PAYOS' && (
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Cấu hình PayOS</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Client ID</label>
                <input
                  type="text"
                  value={gatewayConfigs.PAYOS.clientId}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    PAYOS: { ...prev.PAYOS, clientId: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nhập Client ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">API Key</label>
                <input
                  type="password"
                  value={gatewayConfigs.PAYOS.apiKey}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    PAYOS: { ...prev.PAYOS, apiKey: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nhập API Key"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Checksum Key</label>
                <input
                  type="password"
                  value={gatewayConfigs.PAYOS.checksumKey}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    PAYOS: { ...prev.PAYOS, checksumKey: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nhập Checksum Key"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Môi trường</label>
                <select
                  value={gatewayConfigs.PAYOS.environment}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    PAYOS: { ...prev.PAYOS, environment: e.target.value as 'sandbox' | 'production' }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="sandbox">Sandbox (Test)</option>
                  <option value="production">Production (Live)</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {selectedGateway === 'MOMO' && (
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Cấu hình MoMo</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Partner Code</label>
                <input
                  type="text"
                  value={gatewayConfigs.MOMO.partnerCode}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    MOMO: { ...prev.MOMO, partnerCode: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nhập Partner Code"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Access Key</label>
                <input
                  type="password"
                  value={gatewayConfigs.MOMO.accessKey}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    MOMO: { ...prev.MOMO, accessKey: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nhập Access Key"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Secret Key</label>
                <input
                  type="password"
                  value={gatewayConfigs.MOMO.secretKey}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    MOMO: { ...prev.MOMO, secretKey: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nhập Secret Key"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Môi trường</label>
                <select
                  value={gatewayConfigs.MOMO.environment}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    MOMO: { ...prev.MOMO, environment: e.target.value as 'sandbox' | 'production' }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="sandbox">Sandbox (Test)</option>
                  <option value="production">Production (Live)</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {selectedGateway === 'VNPAY' && (
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Cấu hình VNPay</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">TMN Code</label>
                <input
                  type="text"
                  value={gatewayConfigs.VNPAY.tmnCode}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    VNPAY: { ...prev.VNPAY, tmnCode: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nhập TMN Code"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Secret Key</label>
                <input
                  type="password"
                  value={gatewayConfigs.VNPAY.secretKey}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    VNPAY: { ...prev.VNPAY, secretKey: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nhập Secret Key"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Môi trường</label>
                <select
                  value={gatewayConfigs.VNPAY.environment}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    VNPAY: { ...prev.VNPAY, environment: e.target.value as 'sandbox' | 'production' }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="sandbox">Sandbox (Test)</option>
                  <option value="production">Production (Live)</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {selectedGateway === 'ZALOPAY' && (
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Cấu hình ZaloPay</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">App ID</label>
                <input
                  type="text"
                  value={gatewayConfigs.ZALOPAY.appId}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    ZALOPAY: { ...prev.ZALOPAY, appId: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nhập App ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Key 1</label>
                <input
                  type="password"
                  value={gatewayConfigs.ZALOPAY.key1}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    ZALOPAY: { ...prev.ZALOPAY, key1: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nhập Key 1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Key 2</label>
                <input
                  type="password"
                  value={gatewayConfigs.ZALOPAY.key2}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    ZALOPAY: { ...prev.ZALOPAY, key2: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nhập Key 2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Môi trường</label>
                <select
                  value={gatewayConfigs.ZALOPAY.environment}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    ZALOPAY: { ...prev.ZALOPAY, environment: e.target.value as 'sandbox' | 'production' }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="sandbox">Sandbox (Test)</option>
                  <option value="production">Production (Live)</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {selectedGateway === 'BANK_TRANSFER' && (
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Cấu hình Chuyển khoản ngân hàng</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tên ngân hàng</label>
                <input
                  type="text"
                  value={gatewayConfigs.BANK_TRANSFER.bankName}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    BANK_TRANSFER: { ...prev.BANK_TRANSFER, bankName: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Vietcombank"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Số tài khoản</label>
                <input
                  type="text"
                  value={gatewayConfigs.BANK_TRANSFER.accountNumber}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    BANK_TRANSFER: { ...prev.BANK_TRANSFER, accountNumber: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="1234567890"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tên chủ tài khoản</label>
                <input
                  type="text"
                  value={gatewayConfigs.BANK_TRANSFER.accountName}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    BANK_TRANSFER: { ...prev.BANK_TRANSFER, accountName: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="NGUYEN VAN A"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">QR Code (tùy chọn)</label>
                <input
                  type="text"
                  value={gatewayConfigs.BANK_TRANSFER.qrCode || ''}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    BANK_TRANSFER: { ...prev.BANK_TRANSFER, qrCode: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="URL hoặc base64 của QR code"
                />
              </div>
            </div>
          </div>
        )}

        {selectedGateway === 'VIETQR' && (
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Cấu hình VietQR</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Username (chúng ta cung cấp cho VietQR)</label>
                <input
                  type="text"
                  value={gatewayConfigs.VIETQR.username}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    VIETQR: { ...prev.VIETQR, username: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Tên đăng nhập của bạn (tự đặt)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password (chúng ta cung cấp cho VietQR)</label>
                <input
                  type="password"
                  value={gatewayConfigs.VIETQR.password}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    VIETQR: { ...prev.VIETQR, password: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Mật khẩu của bạn (tự đặt)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Merchant Username (VietQR cung cấp)</label>
                <input
                  type="text"
                  value={gatewayConfigs.VIETQR.merchantUsername}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    VIETQR: { ...prev.VIETQR, merchantUsername: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="username từ VietQR"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Merchant Password (VietQR cung cấp)</label>
                <input
                  type="password"
                  value={gatewayConfigs.VIETQR.merchantPassword}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    VIETQR: { ...prev.VIETQR, merchantPassword: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="password từ VietQR"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mã ngân hàng của chúng ta</label>
                <input
                  type="text"
                  value={gatewayConfigs.VIETQR.bankCode}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    VIETQR: { ...prev.VIETQR, bankCode: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="VCB, TCB, MB, etc."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Số tài khoản của chúng ta</label>
                <input
                  type="text"
                  value={gatewayConfigs.VIETQR.bankAccount}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    VIETQR: { ...prev.VIETQR, bankAccount: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="1234567890"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tên chủ tài khoản của chúng ta</label>
                <input
                  type="text"
                  value={gatewayConfigs.VIETQR.userBankName}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    VIETQR: { ...prev.VIETQR, userBankName: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="NGUYEN VAN A"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Môi trường</label>
                <select
                  value={gatewayConfigs.VIETQR.environment}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    VIETQR: { ...prev.VIETQR, environment: e.target.value as 'sandbox' | 'production' }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="sandbox">Sandbox (Test)</option>
                  <option value="production">Production (Live)</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {selectedGateway === 'SHINHAN' && (
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Cấu hình Shinhan Bank (Virtual Account)</h4>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <p className="font-medium mb-2">Cấu hình một lần cho operator (tất cả chung cư):</p>
              <ol className="list-decimal ml-5 space-y-1">
                <li>Chọn <strong>Cài đặt cá nhân</strong> (operator) hoặc <strong>Cài đặt toàn cục</strong> (SUPERADMIN)</li>
                <li>Tích <strong>Kích hoạt</strong> trên thẻ Shinhan Bank phía trên</li>
                <li>Điền Client ID, tài khoản mẹ, VA Prefix (sandbox), khóa… rồi bấm <strong>Lưu</strong></li>
                <li>Số VA cố định từng tòa nhà (go-live): tab <strong>Cài đặt QR thanh toán</strong> → chọn tòa nhà → <strong>Số VA Shinhan</strong></li>
              </ol>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 space-y-2">
              <p className="font-medium text-gray-900">Webhook đăng ký với Shinhan</p>
              <p>
                <strong>Không dùng VA (chuyển vào tài khoản mẹ):</strong>{' '}
                <code className="bg-white px-2 py-1 rounded border break-all">
                  {SHINHAN_WEBHOOK_BALANCE_NOTICE}
                </code>
              </p>
              <p>
                <strong>Dùng VA (sau này):</strong>{' '}
                <code className="bg-white px-2 py-1 rounded border break-all">
                  {SHINHAN_WEBHOOK_VA_NOTIFY}
                </code>
              </p>
              <p className="text-xs text-gray-500">
                Hai URL phục vụ hai loại thông báo khác nhau — có thể đăng ký cả hai. URL /notify đã gửi trước vẫn giữ
                cho tương lai khi bật VA; hiện tại cần thêm /balance-notice để nhận tiền vào tài khoản mẹ.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Client ID (CMS ID / PARTNER_ID)</label>
                <input
                  type="text"
                  value={gatewayConfigs.SHINHAN.clientId}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    SHINHAN: { ...prev.SHINHAN, clientId: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="SEM01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Client Secret (Cusno / CIF)</label>
                <input
                  type="password"
                  value={gatewayConfigs.SHINHAN.clientSecret}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    SHINHAN: { ...prev.SHINHAN, clientSecret: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="2801521345"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tài khoản mẹ (VAC_MO_ACNO)</label>
                <input
                  type="text"
                  value={gatewayConfigs.SHINHAN.motherAccountNumber}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    SHINHAN: { ...prev.SHINHAN, motherAccountNumber: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="700015066669"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  VA Prefix (Shinhan cấp) — tùy chọn
                </label>
                <input
                  type="text"
                  value={gatewayConfigs.SHINHAN.vaPrefix}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    SHINHAN: { ...prev.SHINHAN, vaPrefix: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="A3J (prefix Shinhan cấp)"
                />
              </div>
              <div className="md:col-span-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={gatewayConfigs.SHINHAN.useVirtualAccount ?? !!gatewayConfigs.SHINHAN.vaPrefix}
                    onChange={(e) => setGatewayConfigs(prev => ({
                      ...prev,
                      SHINHAN: {
                        ...prev.SHINHAN,
                        useVirtualAccount: e.target.checked,
                        ...(e.target.checked
                          ? {}
                          : { useBuildingVirtualAccount: false }),
                      },
                    }))}
                  />
                  Sử dụng Virtual Account (VA) — tắt để cư dân chuyển khoản vào tài khoản mẹ
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Khi tắt VA: không gọi API đăng ký VA; QR hiển thị số tài khoản mẹ và nội dung chuyển khoản chứa mã đơn.
                  Đăng ký webhook <code className="bg-gray-100 px-1 rounded">/callback/shinhan/notify</code> với Shinhan.
                </p>
              </div>
              <div className="md:col-span-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={gatewayConfigs.SHINHAN.useBuildingVirtualAccount ?? true}
                    onChange={(e) => setGatewayConfigs(prev => ({
                      ...prev,
                      SHINHAN: { ...prev.SHINHAN, useBuildingVirtualAccount: e.target.checked }
                    }))}
                    disabled={!(gatewayConfigs.SHINHAN.useVirtualAccount ?? !!gatewayConfigs.SHINHAN.vaPrefix)}
                  />
                  VA cố định theo chung cư (API: {'{prefix}'}{'{mã dự án}'} hoặc VA tại quầy)
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Một VA cho mỗi chung cư: đăng ký qua API (prefix + mã dự án) hoặc nhập số VA tại quầy trong Cài đặt QR thanh toán.
                  Đối soát theo nội dung CK. Tắt để dùng VA tạm theo từng phiếu thanh toán (cũ).
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tên merchant (VAC_ETPR_NM)</label>
                <input
                  type="text"
                  value={gatewayConfigs.SHINHAN.merchantName}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    SHINHAN: { ...prev.SHINHAN, merchantName: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="GOLDTEK EBMS"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Môi trường</label>
                <select
                  value={gatewayConfigs.SHINHAN.environment}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    SHINHAN: {
                      ...prev.SHINHAN,
                      environment: e.target.value as 'sandbox' | 'production' | 'dev',
                      mockMode: e.target.value !== 'production' ? prev.SHINHAN.mockMode ?? true : false,
                    }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="dev">Dev (vncmsdev)</option>
                  <option value="sandbox">Sandbox / Test (vncmstst)</option>
                  <option value="production">Production (cms.shinhan.com.vn)</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={gatewayConfigs.SHINHAN.mockMode ?? true}
                    disabled={gatewayConfigs.SHINHAN.environment === 'production'}
                    onChange={(e) => setGatewayConfigs(prev => ({
                      ...prev,
                      SHINHAN: { ...prev.SHINHAN, mockMode: e.target.checked }
                    }))}
                  />
                  Chế độ giả lập (không gọi API Shinhan thật — dùng để test local)
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Khi bật: &quot;Kiểm tra kết nối&quot; chỉ xác thực cấu hình local (key, Client ID...), không gọi Shinhan.
                  API thật chỉ hoạt động từ server có IP đã đăng ký với Shinhan.
                </p>
              </div>
              <div className="md:col-span-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={gatewayEnabled.SHINHAN || false}
                    onChange={(e) => setGatewayEnabled(prev => ({
                      ...prev,
                      SHINHAN: e.target.checked
                    }))}
                  />
                  Kích hoạt Shinhan Bank cho thanh toán cư dân
                </label>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Private Key (PKCS#8 PEM)</label>
                <textarea
                  value={gatewayConfigs.SHINHAN.privateKey}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    SHINHAN: { ...prev.SHINHAN, privateKey: e.target.value }
                  }))}
                  rows={5}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-xs"
                  placeholder="-----BEGIN PRIVATE KEY-----"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Shinhan Public Key (xác thực webhook)</label>
                <textarea
                  value={gatewayConfigs.SHINHAN.shinhanPublicKey || ''}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    SHINHAN: { ...prev.SHINHAN, shinhanPublicKey: e.target.value }
                  }))}
                  rows={5}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-xs"
                  placeholder="-----BEGIN PUBLIC KEY-----"
                />
              </div>
            </div>
          </div>
        )}

        {selectedGateway === 'MB_PAYGATE' && (
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Cấu hình MB Paygate (API chính thống MB Bank)</h4>
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
              <p className="font-medium mb-2">Tích hợp create-order / IPN / truy vấn giao dịch (tham chiếu sPay VietQR Paygate)</p>
              <ul className="list-disc ml-5 space-y-1">
                <li>OAuth2 client credentials → create-order → <code>payment_url</code> hoặc QR</li>
                <li>IPN mặc định: <code>/api/payment-gateways/callback/mb-paygate/ipn</code> (đăng ký với MB hoặc điền URL tùy chỉnh)</li>
                <li><code>orderRefChannel</code> (vd. 4N) → mã đơn <strong>4NQR…</strong> theo hợp đồng MB</li>
              </ul>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Client ID</label>
                <input
                  type="text"
                  value={gatewayConfigs.MB_PAYGATE.clientId}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    MB_PAYGATE: { ...prev.MB_PAYGATE, clientId: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Client Secret</label>
                <input
                  type="password"
                  value={gatewayConfigs.MB_PAYGATE.clientSecret}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    MB_PAYGATE: { ...prev.MB_PAYGATE, clientSecret: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Merchant ID</label>
                <input
                  type="text"
                  value={gatewayConfigs.MB_PAYGATE.merchantId}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    MB_PAYGATE: { ...prev.MB_PAYGATE, merchantId: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Access code</label>
                <input
                  type="text"
                  value={gatewayConfigs.MB_PAYGATE.accessCode}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    MB_PAYGATE: { ...prev.MB_PAYGATE, accessCode: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Secret key (MAC)</label>
                <input
                  type="password"
                  value={gatewayConfigs.MB_PAYGATE.secretKey}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    MB_PAYGATE: { ...prev.MB_PAYGATE, secretKey: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Môi trường</label>
                <select
                  value={gatewayConfigs.MB_PAYGATE.environment}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    MB_PAYGATE: { ...prev.MB_PAYGATE, environment: e.target.value as 'sandbox' | 'production' }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="sandbox">Sandbox</option>
                  <option value="production">Production</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Kênh order reference (orderRefChannel)</label>
                <input
                  type="text"
                  value={gatewayConfigs.MB_PAYGATE.orderRefChannel || ''}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    MB_PAYGATE: { ...prev.MB_PAYGATE, orderRefChannel: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="4N"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Base URL (tùy chọn)</label>
                <input
                  type="text"
                  value={gatewayConfigs.MB_PAYGATE.baseUrl || ''}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    MB_PAYGATE: { ...prev.MB_PAYGATE, baseUrl: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://api-sandbox.mbbank.com.vn"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">IPN URL (tùy chọn — để MB proxy)</label>
                <input
                  type="text"
                  value={gatewayConfigs.MB_PAYGATE.ipnUrl || ''}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    MB_PAYGATE: { ...prev.MB_PAYGATE, ipnUrl: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="URL đăng ký với MB nếu khác mặc định EBMS"
                />
              </div>
            </div>
          </div>
        )}

        {selectedGateway === 'MBB' && (
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Cấu hình MB Bank Doanh nghiệp (đối soát biến động số dư)</h4>
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
              <p className="font-medium mb-2">MB Bank DN chạy tích hợp trong EBMS API</p>
              <ol className="list-decimal ml-5 space-y-1">
                <li>Đăng ký API key tại <strong>anticaptcha.top</strong> và điền vào trường bên dưới</li>
                <li>Điền User ID, Corp ID, mật khẩu eBank DN và số tài khoản cần đối soát</li>
                <li>Bật <strong>Kích hoạt</strong> — tab <strong>Đối soát</strong> tự đồng bộ khi tìm kiếm (không cần service port 6780)</li>
              </ol>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">User ID (eBank DN)</label>
                <input
                  type="text"
                  value={gatewayConfigs.MBB.username}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    MBB: { ...prev.MBB, username: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mật khẩu</label>
                <input
                  type="password"
                  value={gatewayConfigs.MBB.password}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    MBB: { ...prev.MBB, password: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Corp ID</label>
                <input
                  type="text"
                  value={gatewayConfigs.MBB.corpId}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    MBB: { ...prev.MBB, corpId: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Số tài khoản đối soát</label>
                <input
                  type="text"
                  value={gatewayConfigs.MBB.accountNumber}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    MBB: { ...prev.MBB, accountNumber: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ANTICAPTCHA API key (anticaptcha.top)
                </label>
                <input
                  type="password"
                  value={gatewayConfigs.MBB.anticaptchaApiKey || ''}
                  onChange={(e) => setGatewayConfigs(prev => ({
                    ...prev,
                    MBB: { ...prev.MBB, anticaptchaApiKey: e.target.value }
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="API key giải captcha đăng nhập MB DN"
                />
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 mt-6">
          <button
            onClick={handleSaveGatewaySettings}
            disabled={loading}
            className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 disabled:opacity-50 flex items-center space-x-2 transition-all duration-200"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Đang lưu...</span>
              </>
            ) : (
              <>
                <span>💳</span>
                <span>Lưu cài đặt {selectedGateway}</span>
              </>
            )}
          </button>

          {selectedGateway === 'SHINHAN' && (
            <button
              onClick={handleTestShinhanConnection}
              disabled={testingConnection || loading}
              className="bg-green-500 text-white px-6 py-2 rounded-md hover:bg-green-600 disabled:opacity-50 flex items-center space-x-2 transition-all duration-200"
            >
              {testingConnection ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Đang kiểm tra...</span>
                </>
              ) : (
                <>
                  <span>🔗</span>
                  <span>Kiểm tra kết nối Shinhan</span>
                </>
              )}
            </button>
          )}

          {selectedGateway === 'MB_PAYGATE' && (
            <button
              onClick={handleTestMbPaygateConnection}
              disabled={testingConnection || loading}
              className="bg-green-500 text-white px-6 py-2 rounded-md hover:bg-green-600 disabled:opacity-50 flex items-center space-x-2 transition-all duration-200"
            >
              {testingConnection ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Đang kiểm tra...</span>
                </>
              ) : (
                <>
                  <span>🔗</span>
                  <span>Kiểm tra kết nối MB Paygate</span>
                </>
              )}
            </button>
          )}

          {selectedGateway === 'MBB' && (
            <button
              onClick={handleTestMbbConnection}
              disabled={testingConnection || loading}
              className="bg-green-500 text-white px-6 py-2 rounded-md hover:bg-green-600 disabled:opacity-50 flex items-center space-x-2 transition-all duration-200"
            >
              {testingConnection ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Đang kiểm tra...</span>
                </>
              ) : (
                <>
                  <span>🔗</span>
                  <span>Kiểm tra kết nối MB Bank</span>
                </>
              )}
            </button>
          )}

          <button
            onClick={async () => {
              if (window.confirm(`Bạn có chắc chắn muốn xóa cài đặt ${selectedGateway}?`)) {
                try {
                  await settingsApi.deletePaymentGatewaySettings(
                    selectedGateway,
                    settingsType === 'global',
                    settingsType === 'global' ? undefined : selectedBuildingId || undefined,
                  );
                  toast.success(`✅ Xóa cài đặt ${selectedGateway} thành công!`);
                  loadSettings();
                } catch (error) {
                  toast.error(`❌ Không thể xóa cài đặt ${selectedGateway}`);
                }
              }
            }}
            disabled={loading}
            className="bg-red-500 text-white px-6 py-2 rounded-md hover:bg-red-600 disabled:opacity-50 flex items-center space-x-2 transition-all duration-200"
          >
            <span>🗑️</span>
            <span>Xóa cài đặt</span>
          </button>
        </div>
      </div>
    </div>
  );

  const renderFCMSettings = () => (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          🔔 Cài đặt Firebase Cloud Messaging (FCM)
        </h3>
        <p className="text-sm text-gray-600">
          Cấu hình Firebase Service Account để gửi thông báo đến ứng dụng di động
        </p>
      </div>

      <div className="space-y-4">
        {/* Enable/Disable FCM */}
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            id="fcm-enabled"
            checked={fcmSettings.isEnabled}
            onChange={(e) => setFcmSettings({ ...fcmSettings, isEnabled: e.target.checked })}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="fcm-enabled" className="text-sm font-medium text-gray-700">
            Kích hoạt FCM
          </label>
        </div>

        {/* Service Account JSON */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Firebase Service Account JSON *
          </label>
          <p className="text-xs text-gray-500 mb-2">
            Tải về Service Account JSON từ Firebase Console → Project Settings → Service accounts
          </p>
          {fcmHasStoredAccount && !fcmEditingJson ? (
            <div className="space-y-2">
              <textarea
                readOnly
                value="***************"
                className="w-full h-24 p-3 border border-gray-300 rounded-md bg-gray-50 text-gray-500 font-mono text-sm tracking-widest"
              />
              <p className="text-xs text-gray-500">
                Service Account đã được lưu. Nội dung JSON (gồm private key) không hiển thị vì lý do bảo mật.
              </p>
              <button
                type="button"
                onClick={() => {
                  setFcmEditingJson(true);
                  setFcmJsonInput('');
                }}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Thay đổi Service Account JSON
              </button>
            </div>
          ) : (
            <>
              <textarea
                value={fcmJsonInput}
                onChange={(e) => setFcmJsonInput(e.target.value)}
                placeholder={`{
  "type": "service_account",
  "project_id": "your-project-id",
  "private_key_id": "...",
  "private_key": "-----BEGIN PRIVATE KEY-----\\n...\\n-----END PRIVATE KEY-----\\n",
  "client_email": "firebase-adminsdk-...@your-project-id.iam.gserviceaccount.com",
  "client_id": "...",
  "auth_uri": "https://accounts.google.com/o/oauth2/auth",
  "token_uri": "https://oauth2.googleapis.com/token"
}`}
                className="w-full h-64 p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
              />
              {fcmHasStoredAccount && (
                <button
                  type="button"
                  onClick={() => {
                    setFcmEditingJson(false);
                    setFcmJsonInput('');
                  }}
                  className="mt-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  Hủy thay đổi
                </button>
              )}
              {fcmJsonInput && (
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => {
                      try {
                        const parsed = JSON.parse(fcmJsonInput);
                        setFcmJsonInput(JSON.stringify(parsed, null, 2));
                        toast.success('✅ JSON đã được format');
                      } catch (error) {
                        toast.error('❌ JSON không hợp lệ');
                      }
                    }}
                    className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded"
                  >
                    Format JSON
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Information Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <h4 className="font-medium text-blue-900 mb-2">📋 Hướng dẫn cài đặt:</h4>
          <div className="text-sm text-blue-700 space-y-2">
            <p>1. Truy cập Firebase Console: <code className="bg-blue-100 px-1 rounded">console.firebase.google.com</code></p>
            <p>2. Chọn project → Project Settings → Service accounts</p>
            <p>3. Click "Generate new private key" và tải về file JSON</p>
            <p>4. Copy nội dung file JSON và paste vào ô trên</p>
            <p>5. Bật "Kích hoạt FCM" và lưu cài đặt</p>
          </div>
        </div>

        {/* Current Status */}
        {fcmSettings.isEnabled && fcmHasStoredAccount && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <h4 className="font-medium text-green-900 mb-2">✅ Trạng thái hiện tại:</h4>
            <div className="text-sm text-green-700">
              <p><strong>Project ID:</strong> {fcmSettings.serviceAccount?.project_id || '-'}</p>
              <p><strong>Client Email:</strong> {fcmSettings.serviceAccount?.client_email || '-'}</p>
              <p><strong>Trạng thái:</strong> {fcmSettings.isEnabled ? 'Đã kích hoạt' : 'Chưa kích hoạt'}</p>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4 pt-4">
        <button
          onClick={handleSaveFCMSettings}
          disabled={loading}
          className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 disabled:opacity-50 flex items-center space-x-2 transition-all duration-200"
        >
          {loading ? (
            <>
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Đang lưu...</span>
            </>
          ) : (
            <>
              <span>💾</span>
              <span>Lưu cài đặt FCM</span>
            </>
          )}
        </button>

        <button
          onClick={handleTestFCMConnection}
          disabled={testingConnection || !fcmSettings.isEnabled}
          className="bg-green-500 text-white px-6 py-2 rounded-md hover:bg-green-600 disabled:opacity-50 flex items-center space-x-2 transition-all duration-200"
        >
          {testingConnection ? (
            <>
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Đang kiểm tra...</span>
            </>
          ) : (
            <>
              <span>🧪</span>
              <span>Kiểm tra kết nối</span>
            </>
          )}
        </button>

        <button
          onClick={async () => {
            if (window.confirm('Bạn có chắc chắn muốn xóa cài đặt FCM?')) {
              try {
                await settingsApi.deleteFCMSettings();
                toast.success('✅ Xóa cài đặt FCM thành công!');
                setFcmSettings({ serviceAccount: {}, isEnabled: false });
                setFcmHasStoredAccount(false);
                setFcmEditingJson(false);
                setFcmJsonInput('');
              } catch (error) {
                toast.error('❌ Không thể xóa cài đặt FCM');
              }
            }
          }}
          disabled={loading}
          className="bg-red-500 text-white px-6 py-2 rounded-md hover:bg-red-600 disabled:opacity-50 flex items-center space-x-2 transition-all duration-200"
        >
          <span>🗑️</span>
          <span>Xóa cài đặt</span>
        </button>
      </div>
    </div>
  );

  const renderStorageSettings = () => (
    <div className="space-y-6">
      {/* Settings Type Selector */}
      <div className="space-y-4 border-b border-gray-200 pb-4">
        <div className="flex space-x-4">
          {user?.role === 'SUPERADMIN' && (
            <button
              onClick={() => setSettingsType('global')}
              className={`px-4 py-2 rounded-md ${
                settingsType === 'global'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('globalSettings')}
            </button>
          )}
          <button
            onClick={() => setSettingsType('user')}
            className={`px-4 py-2 rounded-md ${
              settingsType === 'user'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('personalSettings')}
          </button>
        </div>

        {renderScopedBuildingSelector()}

        {settingsType === 'user' && (
          <p className="text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md p-3">
            Form bên dưới chỉ hiển thị cài đặt riêng tại phạm vi đã chọn — không hiển thị giá trị toàn cục.
            Bấm <strong>Xóa cài đặt</strong> để quay về cài đặt cấp cao hơn.
          </p>
        )}

        {settingsType === 'global' && user?.role === 'SUPERADMIN' && (
          <p className="text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md p-3">
            Cài đặt toàn cục làm mặc định khi operator chưa cấu hình riêng.
          </p>
        )}
      </div>

      {settingsType === 'user' && effectiveStorageSettings && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <h4 className="font-medium text-blue-900 mb-2">Đang chạy trên hệ thống:</h4>
          <div className="text-sm text-blue-700">
            <p><strong>Loại:</strong> {effectiveStorageSettings.storageType === 's3' ? 'S3 Compatible' : 'Local Storage'}</p>
            {effectiveStorageSettings.storageType === 's3' && effectiveStorageSettings.s3Config && (
              <p><strong>Endpoint:</strong> {effectiveStorageSettings.s3Config.endpoint || '-'}</p>
            )}
            <p className="text-xs mt-2">
              {hasOwnOperatorStorageSettings
                ? (selectedBuildingId ? 'Đang dùng cài đặt riêng theo tòa nhà' : 'Đang dùng cài đặt operator')
                : selectedBuildingId
                  ? 'Đang dùng cài đặt operator hoặc toàn cục (chưa có cài đặt riêng theo tòa)'
                  : 'Đang dùng cài đặt toàn cục (chưa có cài đặt operator)'}
            </p>
          </div>
        </div>
      )}

      {settingsType === 'user' && !hasOwnOperatorStorageSettings && (
        <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-3">
          Chưa có cài đặt lưu trữ riêng tại phạm vi này. Cấu hình bên dưới và Lưu để tạo cài đặt, hoặc để mặc định và dùng cài đặt cấp cao hơn.
        </p>
      )}

      {/* Storage Type Selection */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Loại lưu trữ</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <label className={`p-4 border rounded-lg cursor-pointer transition-all ${
            storageSettings.storageType === 'local'
              ? 'border-blue-500 bg-blue-50 text-blue-700'
              : 'border-gray-300 hover:border-gray-400'
          }`}>
            <input
              type="radio"
              name="storageType"
              value="local"
              checked={storageSettings.storageType === 'local'}
              onChange={(e) => setStorageSettings(prev => ({
                ...prev,
                storageType: e.target.value as 'local' | 's3'
              }))}
              className="sr-only"
            />
            <div className="flex items-center space-x-3">
              <div className="text-2xl">💾</div>
              <div>
                <div className="font-medium">Local Storage</div>
                <div className="text-sm text-gray-500">Lưu trữ file trên server</div>
              </div>
            </div>
          </label>

          <label className={`p-4 border rounded-lg cursor-pointer transition-all ${
            storageSettings.storageType === 's3'
              ? 'border-blue-500 bg-blue-50 text-blue-700'
              : 'border-gray-300 hover:border-gray-400'
          }`}>
            <input
              type="radio"
              name="storageType"
              value="s3"
              checked={storageSettings.storageType === 's3'}
              onChange={(e) => setStorageSettings(prev => ({
                ...prev,
                storageType: e.target.value as 'local' | 's3'
              }))}
              className="sr-only"
            />
            <div className="flex items-center space-x-3">
              <div className="text-2xl">☁️</div>
              <div>
                <div className="font-medium">S3 Compatible Storage</div>
                <div className="text-sm text-gray-500">Lưu trữ file trên cloud (AWS S3, MinIO, etc.)</div>
              </div>
            </div>
          </label>
        </div>

        {/* S3 Configuration Form */}
        {storageSettings.storageType === 's3' && (
          <div className="space-y-4 border-t pt-6">
            <h4 className="font-medium text-gray-900">Cấu hình S3 Storage</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tên storage *
                </label>
                <input
                  type="text"
                  value={storageSettings.s3Config.name}
                  onChange={(e) => setStorageSettings(prev => ({
                    ...prev,
                    s3Config: { ...prev.s3Config, name: e.target.value }
                  }))}
                  placeholder="My S3 Storage"
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Endpoint *
                </label>
                <input
                  type="text"
                  value={storageSettings.s3Config.endpoint}
                  onChange={(e) => setStorageSettings(prev => ({
                    ...prev,
                    s3Config: { ...prev.s3Config, endpoint: e.target.value }
                  }))}
                  placeholder="https://s3.amazonaws.com"
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Region *
                </label>
                <input
                  type="text"
                  value={storageSettings.s3Config.region}
                  onChange={(e) => setStorageSettings(prev => ({
                    ...prev,
                    s3Config: { ...prev.s3Config, region: e.target.value }
                  }))}
                  placeholder="us-east-1"
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Access Key *
                </label>
                <input
                  type="password"
                  value={storageSettings.s3Config.accessKey}
                  onChange={(e) => setStorageSettings(prev => ({
                    ...prev,
                    s3Config: { ...prev.s3Config, accessKey: e.target.value }
                  }))}
                  placeholder="AKIAIOSFODNN7EXAMPLE"
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Secret Key *
                </label>
                <input
                  type="password"
                  value={storageSettings.s3Config.secretKey}
                  onChange={(e) => setStorageSettings(prev => ({
                    ...prev,
                    s3Config: { ...prev.s3Config, secretKey: e.target.value }
                  }))}
                  placeholder="••••••••••••••••••••••••••••••••"
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bucket Name *
                </label>
                <input
                  type="text"
                  value={storageSettings.s3Config.bucketName}
                  onChange={(e) => setStorageSettings(prev => ({
                    ...prev,
                    s3Config: { ...prev.s3Config, bucketName: e.target.value }
                  }))}
                  placeholder="my-bucket"
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>

            {/* Information Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <h4 className="font-medium text-blue-900 mb-2">📋 Hỗ trợ S3 Compatible Storage:</h4>
              <div className="text-sm text-blue-700 space-y-1">
                <p>• AWS S3</p>
                <p>• MinIO</p>
                <p>• DigitalOcean Spaces</p>
                <p>• Cloudflare R2</p>
                <p>• Backblaze B2</p>
                <p>• Và các dịch vụ S3-compatible khác</p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 mt-6">
          <button
            onClick={handleSaveStorageSettings}
            disabled={loading}
            className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 disabled:opacity-50 flex items-center space-x-2 transition-all duration-200"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Đang lưu...</span>
              </>
            ) : (
              <>
                <span>💾</span>
                <span>Lưu cài đặt</span>
              </>
            )}
          </button>

          <button
            onClick={() => handleDeleteSettings('storage')}
            disabled={loading}
            className="bg-red-500 text-white px-6 py-2 rounded-md hover:bg-red-600 disabled:opacity-50 flex items-center space-x-2 transition-all duration-200"
          >
            <span>🗑️</span>
            <span>Xóa cài đặt</span>
          </button>
        </div>
      </div>
    </div>
  );

  const renderDecimalSettings = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="w-5 h-5 text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Cấu hình số thập phân</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>Cấu hình số chữ số thập phân hiển thị cho số tiền và VAT. Mặc định là 0 cho VND (không có thập phân).</p>
            </div>
          </div>
        </div>
      </div>

      {/* Decimal Settings Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Cài đặt số thập phân</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Amount Decimal Places */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Số chữ số thập phân cho số tiền
            </label>
            <select
              value={decimalSettings.amountDecimalPlaces}
              onChange={(e) => setDecimalSettings(prev => ({
                ...prev,
                amountDecimalPlaces: parseInt(e.target.value)
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={0}>0 - Không có thập phân (VND)</option>
              <option value={1}>1 - Một chữ số thập phân</option>
              <option value={2}>2 - Hai chữ số thập phân (USD, EUR)</option>
              <option value={3}>3 - Ba chữ số thập phân</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Ví dụ: {decimalSettings.amountDecimalPlaces === 0 ? '1,000' : 
                     decimalSettings.amountDecimalPlaces === 1 ? '1,000.0' :
                     decimalSettings.amountDecimalPlaces === 2 ? '1,000.00' : '1,000.000'}
            </p>
          </div>

          {/* VAT Decimal Places */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Số chữ số thập phân cho VAT
            </label>
            <select
              value={decimalSettings.vatDecimalPlaces}
              onChange={(e) => setDecimalSettings(prev => ({
                ...prev,
                vatDecimalPlaces: parseInt(e.target.value)
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={0}>0 - Không có thập phân (VND)</option>
              <option value={1}>1 - Một chữ số thập phân</option>
              <option value={2}>2 - Hai chữ số thập phân (USD, EUR)</option>
              <option value={3}>3 - Ba chữ số thập phân</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Ví dụ: {decimalSettings.vatDecimalPlaces === 0 ? '100' : 
                     decimalSettings.vatDecimalPlaces === 1 ? '100.0' :
                     decimalSettings.vatDecimalPlaces === 2 ? '100.00' : '100.000'}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 mt-6">
          <button
            onClick={handleSaveDecimalSettings}
            disabled={loading}
            className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 disabled:opacity-50 flex items-center space-x-2 transition-all duration-200"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Đang lưu...</span>
              </>
            ) : (
              <>
                <span>💰</span>
                <span>Lưu cài đặt</span>
              </>
            )}
          </button>

          <button
            onClick={async () => {
              if (window.confirm('Bạn có chắc chắn muốn xóa cài đặt decimal?')) {
                try {
                  await settingsApi.deleteDecimalSettings();
                  setDecimalSettings({ amountDecimalPlaces: 0, vatDecimalPlaces: 0 });
                  toast.success('✅ Xóa cài đặt decimal thành công!');
                  loadSettings();
                } catch (error) {
                  toast.error('❌ Không thể xóa cài đặt decimal');
                }
              }
            }}
            disabled={loading}
            className="bg-red-500 text-white px-6 py-2 rounded-md hover:bg-red-600 disabled:opacity-50 flex items-center space-x-2 transition-all duration-200"
          >
            <span>🗑️</span>
            <span>Xóa cài đặt</span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('settingsTitle')}</h1>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('email')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'email'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t('emailSettings')}
          </button>
          <button
            onClick={() => setActiveTab('storage')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'storage'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Cấu hình lưu trữ
          </button>
          <button
            onClick={() => setActiveTab('decimal')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'decimal'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Cài đặt số thập phân
          </button>
          <button
            onClick={() => setActiveTab('gateways')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'gateways'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Cấu hình cổng thanh toán
          </button>
          <button
            onClick={() => setActiveTab('qr')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'qr'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t('qrSettings')}
          </button>
          <button
            onClick={() => setActiveTab('logo')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'logo'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t('logoSettings')}
          </button>
          {user?.role === 'SUPERADMIN' && (
            <button
              onClick={() => setActiveTab('fcm')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'fcm'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Cài đặt thông báo
            </button>
          )}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white shadow rounded-lg p-6">
        {activeTab === 'email' && renderEmailSettings()}
        {activeTab === 'storage' && renderStorageSettings()}
        {activeTab === 'decimal' && renderDecimalSettings()}
        {activeTab === 'gateways' && renderPaymentGatewaySettings()}
        {activeTab === 'qr' && (
          <QrSettingsPanel
            settingsType={settingsType}
            setSettingsType={setSettingsType}
            selectedBuildingId={selectedBuildingId}
            setSelectedBuildingId={setSelectedBuildingId}
            buildings={buildings}
            isSuperAdmin={user?.role === 'SUPERADMIN'}
          />
        )}
        {activeTab === 'logo' && (
          <LogoSettingsPanel
            settingsType={settingsType}
            setSettingsType={setSettingsType}
            isSuperAdmin={user?.role === 'SUPERADMIN'}
          />
        )}
        {activeTab === 'fcm' && renderFCMSettings()}
      </div>
    </div>
  );
};

export default Settings; 